/**
 * Skärmdumpar till feedbacken. Bilderna skalas ned i webbläsaren innan de skickas: en helskärms-
 * dump från en 4K-skärm är flera MB som PNG, och servern tar högst några MB per bild. Små bilder
 * skickas oförändrade så att text i dem förblir skarp.
 */
export interface FeedbackScreenshot {
  id: string;
  fileName: string;
  contentType: string;
  /** Bildens bytes som base64, utan data:-prefix — det servern vill ha. */
  base64Data: string;
  /** data:-URL för miniatyren i rutan. */
  previewUrl: string;
  sizeBytes: number;
}

export const MAX_SCREENSHOTS = 5;
const SUPPORTED_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
const MAX_DIMENSION = 1920;
const KEEP_ORIGINAL_BELOW_BYTES = 1_000_000;
const JPEG_QUALITY = 0.85;

export const isSupportedImage = (file: File): boolean => SUPPORTED_TYPES.includes(file.type);

/** Plockar bildfilerna ur ett klistra in-event (t.ex. efter Win+Shift+S eller Cmd+Ctrl+Shift+4). */
export const imagesFromClipboard = (data: DataTransfer | null): File[] =>
  Array.from(data?.items ?? [])
    .filter((item) => item.kind === 'file' && item.type.startsWith('image/'))
    .map((item) => item.getAsFile())
    .filter((file): file is File => !!file);

/** Bytestorleken ur en base64-sträng, utan att avkoda den. */
export const base64ByteLength = (base64: string): number => {
  const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0;
  return Math.floor((base64.length * 3) / 4) - padding;
};

/** Delar en data:-URL i typ och base64-data. */
export const splitDataUrl = (
  dataUrl: string
): { contentType: string; base64Data: string } | null => {
  const match = /^data:([^;,]+);base64,(.*)$/.exec(dataUrl);
  return match ? { contentType: match[1], base64Data: match[2] } : null;
};

const readAsDataUrl = (file: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Bilden gick inte att läsa.'));
    img.src = src;
  });

let idCounter = 0;

export const prepareScreenshot = async (file: File): Promise<FeedbackScreenshot> => {
  const originalUrl = await readAsDataUrl(file);
  const img = await loadImage(originalUrl);

  let dataUrl = originalUrl;
  const tooLarge = img.width > MAX_DIMENSION || img.height > MAX_DIMENSION;
  if (tooLarge || file.size > KEEP_ORIGINAL_BELOW_BYTES) {
    const scale = Math.min(1, MAX_DIMENSION / Math.max(img.width, img.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    const ctx = canvas.getContext('2d');
    if (ctx) {
      // Vit botten: JPEG saknar genomskinlighet, och svart bakom text blir oläsligt.
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      dataUrl = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
    }
  }

  const parts = splitDataUrl(dataUrl);
  if (!parts) throw new Error('Bilden gick inte att läsa.');

  idCounter += 1;
  return {
    id: `shot-${Date.now()}-${idCounter}`,
    fileName: file.name || 'urklipp',
    contentType: parts.contentType,
    base64Data: parts.base64Data,
    previewUrl: dataUrl,
    sizeBytes: base64ByteLength(parts.base64Data),
  };
};
